import { useState, useRef, useEffect, useCallback } from 'react';
import apiClient from '../services/apiClient';

/**
 * useWhisperRecorder (#1) — programmatic mic recording + Whisper transcription
 * for the hands-free conversation loop.
 *
 * This is a hook version of the capture → 16kHz-WAV resample → /whisper/transcribe
 * flow in components/WhisperRecording.js. It's deliberately a separate copy so the
 * existing WhisperRecording component (used in the normal chat input) is untouched.
 *
 * Unlike the component, recording is driven by start()/stop() so a controller can
 * run the loop. The transcript is delivered via the onResult callback.
 *
 * Usage:
 *   const rec = useWhisperRecorder({ sessionId, targetLanguage,
 *                                    onResult: (text, audioId) => {...},
 *                                    onError: (e) => {...} });
 *   rec.startRecording(); ... rec.stopRecording();
 *
 * Optional auto-stop (all off by default; pass milliseconds to enable):
 *   maxDurationMs — hard cap; stops and SENDS what was recorded.
 *   silenceMs     — after the learner has spoken, stop and send once they've
 *                   been quiet this long.
 *   noSpeechMs    — if nothing is said at all for this long, stop and DISCARD
 *                   (so silence is never sent to Whisper, which invents text).
 *   onAutoStop(reason) — 'max' | 'silence' | 'no_speech'.
 * Silence detection reads mic levels through an AudioContext that must be
 * unlocked inside a user gesture: call primeAudio() from a tap handler. If the
 * context isn't running (e.g. iOS never unlocked it), silence/no-speech
 * detection is skipped and only the hard cap applies.
 * secondsLeft is non-null during the last few seconds before the hard cap.
 */
const SPEECH_RMS = 0.02;      // mic level that counts as speaking
const MIN_SPEECH_MS = 300;    // this much speech before silence can end the turn
const COUNTDOWN_S = 5;        // show a countdown for the last N seconds of the cap
const TICK_MS = 100;

export default function useWhisperRecorder({
  sessionId, targetLanguage, onResult, onError, onAutoStop,
  maxDurationMs = 0, silenceMs = 0, noSpeechMs = 0,
}) {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(null);

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const audioChunksRef = useRef([]);
  // Keep the latest callbacks without forcing start/stop to be re-created.
  const onResultRef = useRef(onResult);
  const onErrorRef = useRef(onError);
  const onAutoStopRef = useRef(onAutoStop);
  useEffect(() => { onResultRef.current = onResult; }, [onResult]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);
  useEffect(() => { onAutoStopRef.current = onAutoStop; }, [onAutoStop]);
  const optsRef = useRef({ maxDurationMs, silenceMs, noSpeechMs });
  useEffect(() => { optsRef.current = { maxDurationMs, silenceMs, noSpeechMs }; },
    [maxDurationMs, silenceMs, noSpeechMs]);

  // Auto-stop monitoring (level meter + timers).
  const monitorCtxRef = useRef(null);     // gesture-unlocked AudioContext for level metering
  const monitorSourceRef = useRef(null);
  const monitorTimerRef = useRef(null);
  const discardCurrentRef = useRef(null); // marks the in-flight recording as discarded
  const stopRef = useRef(null);

  // Create/resume the metering AudioContext. Call from a user gesture (tap).
  const primeAudio = useCallback(() => {
    try {
      if (!monitorCtxRef.current || monitorCtxRef.current.state === 'closed') {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        monitorCtxRef.current = new Ctx();
      }
      if (monitorCtxRef.current.state === 'suspended') {
        const p = monitorCtxRef.current.resume();
        if (p && p.catch) p.catch(() => {});
      }
    } catch (e) { /* metering unavailable — hard cap still applies */ }
  }, []);

  const clearMonitor = useCallback(() => {
    if (monitorTimerRef.current) { clearInterval(monitorTimerRef.current); monitorTimerRef.current = null; }
    if (monitorSourceRef.current) {
      try { monitorSourceRef.current.disconnect(); } catch (e) { /* noop */ }
      monitorSourceRef.current = null;
    }
    setSecondsLeft(null);
  }, []);

  const startMonitoring = useCallback((stream) => {
    clearMonitor();
    const { maxDurationMs: maxMs, silenceMs: quietMs, noSpeechMs: idleMs } = optsRef.current;
    if (!maxMs && !quietMs && !idleMs) return;

    let analyser = null;
    let buf = null;
    const ctx = monitorCtxRef.current;
    if ((quietMs || idleMs) && ctx && ctx.state === 'running') {
      try {
        const src = ctx.createMediaStreamSource(stream);
        analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        src.connect(analyser);
        monitorSourceRef.current = src;
        buf = new Uint8Array(analyser.fftSize);
      } catch (e) { analyser = null; }
    }

    const startedAt = Date.now();
    let sawSignal = false;   // guards against a silent/broken meter pausing a real speaker
    let speechMs = 0;
    let lastVoiceAt = 0;

    const autoStop = (reason) => {
      clearMonitor();
      if (stopRef.current) stopRef.current({ discard: reason === 'no_speech' });
      if (onAutoStopRef.current) onAutoStopRef.current(reason);
    };

    monitorTimerRef.current = setInterval(() => {
      const now = Date.now();
      const elapsed = now - startedAt;
      if (analyser) {
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
        const rms = Math.sqrt(sum / buf.length);
        if (rms > 0.002) sawSignal = true;
        if (rms > SPEECH_RMS) { speechMs += TICK_MS; lastVoiceAt = now; }
      }
      const spoke = speechMs >= MIN_SPEECH_MS;

      if (maxMs) {
        const left = Math.ceil((maxMs - elapsed) / 1000);
        setSecondsLeft(left <= COUNTDOWN_S ? Math.max(left, 0) : null);
        if (elapsed >= maxMs) { autoStop('max'); return; }
      }
      if (analyser && sawSignal) {
        if (quietMs && spoke && now - lastVoiceAt >= quietMs) { autoStop('silence'); return; }
        if (idleMs && !spoke && elapsed >= idleMs) { autoStop('no_speech'); return; }
      }
    }, TICK_MS);
  }, [clearMonitor]);

  const transcribeAudio = useCallback(async (audioBlob) => {
    if (!audioBlob || audioBlob.size === 0) {
      setIsProcessing(false);
      if (onErrorRef.current) onErrorRef.current(new Error('No audio recorded'));
      return;
    }
    try {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      const formData = new FormData();
      if (isIOS) {
        let iosBlob = audioBlob;
        if (audioBlob.type !== 'audio/mp3' && audioBlob.type !== 'audio/mpeg') {
          try {
            iosBlob = new Blob([await audioBlob.arrayBuffer()], { type: 'audio/mpeg' });
          } catch (e) { iosBlob = audioBlob; }
        }
        formData.append('audio', new File([iosBlob], 'recording.mp3', { type: 'audio/mpeg' }));
      } else {
        formData.append('audio', audioBlob, 'recording.mp3');
      }
      formData.append('sessionId', sessionId);
      formData.append('language', targetLanguage);

      const response = await apiClient.audio.transcribeWhisper(formData);
      const transcription = response.data.transcription;
      const audioId = response.data.audio_id;
      if (onResultRef.current) onResultRef.current(transcription, audioId);
    } catch (error) {
      console.error('useWhisperRecorder: transcription failed', error);
      if (onErrorRef.current) onErrorRef.current(error);
    } finally {
      setIsProcessing(false);
    }
  }, [sessionId, targetLanguage]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
      });
      streamRef.current = stream;
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      let discarded = false;
      discardCurrentRef.current = () => { discarded = true; };

      mediaRecorder.onstop = async () => {
        if (discarded) {
          setIsProcessing(false);
          try { audioContext.close(); } catch (e) { /* noop */ }
          return;
        }
        const originalBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        try {
          const arrayBuffer = await originalBlob.arrayBuffer();
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          const offlineContext = new OfflineAudioContext(1, Math.ceil(audioBuffer.duration * 16000), 16000);
          const source = offlineContext.createBufferSource();
          source.buffer = audioBuffer;
          const gainNode = offlineContext.createGain();
          gainNode.gain.value = 2.5;
          const highPassFilter = offlineContext.createBiquadFilter();
          highPassFilter.type = 'highpass';
          highPassFilter.frequency.value = 80;
          const compressor = offlineContext.createDynamicsCompressor();
          compressor.threshold.value = -50;
          compressor.knee.value = 40;
          compressor.ratio.value = 12;
          compressor.attack.value = 0;
          compressor.release.value = 0.25;
          source.connect(highPassFilter);
          highPassFilter.connect(gainNode);
          gainNode.connect(compressor);
          compressor.connect(offlineContext.destination);
          source.start(0);
          const resampledBuffer = await offlineContext.startRendering();
          const wav = bufferToWav(resampledBuffer);
          await transcribeAudio(new Blob([wav], { type: 'audio/wav' }));
        } catch (err) {
          console.error('useWhisperRecorder: resample failed, sending original', err);
          await transcribeAudio(originalBlob);
        } finally {
          try { audioContext.close(); } catch (e) { /* noop */ }
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      startMonitoring(stream);
    } catch (error) {
      console.error('useWhisperRecorder: could not start recording', error);
      setIsRecording(false);
      if (onErrorRef.current) onErrorRef.current(error);
    }
  }, [transcribeAudio, startMonitoring]);

  // stopRecording({ discard: true }) stops without transcribing.
  const stopRecording = useCallback((opts = {}) => {
    clearMonitor();
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== 'inactive') {
      if (opts.discard && discardCurrentRef.current) discardCurrentRef.current();
      else setIsProcessing(true);
      mr.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
    setIsRecording(false);
  }, [clearMonitor]);
  stopRef.current = stopRecording;

  // Cleanup on unmount.
  useEffect(() => () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      if (monitorTimerRef.current) clearInterval(monitorTimerRef.current);
      if (monitorCtxRef.current && monitorCtxRef.current.state !== 'closed') monitorCtxRef.current.close();
    } catch (e) { /* noop */ }
  }, []);

  return { isRecording, isProcessing, startRecording, stopRecording, primeAudio, secondsLeft };
}

// AudioBuffer -> 16-bit PCM WAV (copied from WhisperRecording.js, proven format).
function bufferToWav(buffer) {
  const numOfChannels = buffer.numberOfChannels;
  const length = buffer.length * numOfChannels * 2;
  const sampleRate = buffer.sampleRate;
  const wav = new DataView(new ArrayBuffer(44 + length));
  writeString(wav, 0, 'RIFF');
  wav.setUint32(4, 36 + length, true);
  writeString(wav, 8, 'WAVE');
  writeString(wav, 12, 'fmt ');
  wav.setUint32(16, 16, true);
  wav.setUint16(20, 1, true);
  wav.setUint16(22, numOfChannels, true);
  wav.setUint32(24, sampleRate, true);
  wav.setUint32(28, sampleRate * numOfChannels * 2, true);
  wav.setUint16(32, numOfChannels * 2, true);
  wav.setUint16(34, 16, true);
  writeString(wav, 36, 'data');
  wav.setUint32(40, length, true);
  const channels = [];
  for (let i = 0; i < numOfChannels; i++) channels.push(buffer.getChannelData(i));
  let offset = 44;
  for (let i = 0; i < buffer.length; i++) {
    for (let j = 0; j < numOfChannels; j++) {
      const sample = Math.max(-1, Math.min(1, channels[j][i]));
      const value = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
      wav.setInt16(offset, value, true);
      offset += 2;
    }
  }
  return wav;
}

function writeString(view, offset, string) {
  for (let i = 0; i < string.length; i++) view.setUint8(offset + i, string.charCodeAt(i));
}
